import { useState } from 'react';

// Services
import { AlbumFunnelModalService } from '@/components/pages/albums/AlbumFunnelModal/services/AlbumFunnelModalService';

import { useFeatureFlag } from '@/utils/hooks';
// Hooks
import { NEXT_STEP, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

// Lib
import { ALBUM_TYPE_MANUAL, albumTypes } from '@/lib/albums/albumFunnel/types';

import Button from '@/components/base/Button';
// Components
import Label from '@/components/base/Label';
import Modal from '@/components/base/Modal';
import AlbumTypeCard from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/SelectTypeStep/AlbumTypeCard';
import { InputAlbumName } from '@/components/pages/albums/AlbumFunnelModal/components/InputAlbumName';

export const SelectTypeStep = ({
    dismiss = () => {
        /** */
    }
}) => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();
    const [activeType, setActiveType] = useState(albumInfo?.type ?? ALBUM_TYPE_MANUAL);
    const [nameError, setNameError] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const isGroup = useIsGroup();
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const campaignFeatureFlagEnabled = useFeatureFlag('campaign_media_limit_500_active');

    const handleNameChange = () => {
        setNameError(null);
    };

    const handleNext = async () => {
        if (!albumInfo?.name || albumInfo.name.trim() === '') {
            return;
        }

        setIsValidating(true);
        setNameError(null);

        try {
            const isAlbumNameDuplicated = await AlbumFunnelModalService.isAlbumNameDuplicated({
                name: albumInfo.name,
                activeSocialProfileId: isGroup ? undefined : activeSocialProfile?.id,
                activeGroupId: isGroup ? activeGroup?.id : undefined
            });

            if (isAlbumNameDuplicated) {
                setNameError(t('modal.update_album_error_name_unique'));
                return;
            }

            dispatch({
                type: NEXT_STEP,
                payload: activeType
            });
        } catch (error) {
            console.error(error);

            setNameError(t('modal.update_album_error_name_generic'));
        } finally {
            setIsValidating(false);
        }
    };

    return (
        <div className="w-[calc(100vw-24px)] sm:max-w-[37.5rem]">
            <Modal.Body>
                <Modal.Title>{t('modal.create_modal.title')}</Modal.Title>
                {campaignFeatureFlagEnabled && (
                    <p className="pt-m text-center font-GMedium text-base text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {t('banner.limit_to_500_media')}
                    </p>
                )}
                <Modal.Content className="mt-8 space-y-8 rounded-md bg-icoGray-100 p-8 pb-5 pt-5 dark:bg-icoGray-400 sm:pb-8 sm:pt-8">
                    <InputAlbumName error={nameError} onChange={handleNameChange} />

                    <div>
                        <Label required className="mb-2.5">
                            {t('content:modal.create_modal.select_type')}
                        </Label>
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            {albumTypes?.map((albumType, index) => (
                                <AlbumTypeCard
                                    key={index}
                                    data-testid={`card-album-type-${albumType.type}`}
                                    image={albumType.image}
                                    title={t(albumType.title)}
                                    description={t(albumType.description)}
                                    isSelected={activeType === albumType.type}
                                    onClick={() => setActiveType(albumType?.type ?? 'manual')}
                                />
                            ))}
                        </div>
                    </div>
                </Modal.Content>
            </Modal.Body>
            <Modal.Footer>
                <button
                    onClick={dismiss}
                    className="font-GMedium text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf">
                    {t('commons:cancel')}
                </button>
                <Button
                    id="select-campaign-type-next-step-button"
                    className="w-48"
                    disabled={!albumInfo?.name?.trim() || !!nameError || isValidating}
                    isLoading={isValidating}
                    onClick={handleNext}>
                    {t('commons:next')}
                </Button>
            </Modal.Footer>
        </div>
    );
};
